import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search,
  Loader2,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/api';

export default function SearchOperatorsPage() {
  const [searchParams, setSearchParams] = useState({
    businessName: '',
    states: [],
  });
  const [operators, setOperators] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [selectedOperatorDetails, setSelectedOperatorDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState({
    totalAssignedThisMonth: 0,
    remainingQuota: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0,
  });
  const [facets, setFacets] = useState({
    states: [],
    licenseTypes: [],
    categories: [],
  });

  const allStates = [
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'OR', name: 'Oregon' },
    { code: 'WA', name: 'Washington' },
    { code: 'NV', name: 'Nevada' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'IL', name: 'Illinois' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NY', name: 'New York' },
    { code: 'FL', name: 'Florida' },
    { code: 'OK', name: 'Oklahoma' },
  ];

  // Load operators on mount
  useEffect(() => {
    loadOperators();
  }, []);

  const loadOperators = async (page = 1) => {
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pagination.pageSize);
      params.append('sortDirection', 'asc');
      params.append('sortBy', 'state');
      
      // Add search filters if any
      if (searchParams.businessName) {
        params.append('businessName', searchParams.businessName);
      }
      searchParams.states.forEach(state => {
        params.append('States', state);
      });

      const response = await apiClient.get(`/operators/search?${params.toString()}`);
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        setOperators(data.results || []);
        
        // Update pagination info
        if (data.pagination) {
          setPagination({
            page: data.pagination.page,
            pageSize: data.pagination.pageSize,
            totalRecords: data.pagination.totalRecords,
            totalPages: data.pagination.totalPages,
          });
        }
        
        // Update facets
        if (data.facets) {
          setFacets(data.facets);
        }
        
        toast.success(`Loaded ${data.results.length} operators`);
      } else {
        setOperators([]);
        toast.error('No operators found');
      }
    } catch (error) {
      console.error('Load operators error:', error);
      toast.error('Failed to load operators');
      setOperators([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchParams.businessName && searchParams.states.length === 0) {
      toast.error('Please enter a business name or select at least one state');
      return;
    }

    setIsSearching(true);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    try {
      await loadOperators(1);
    } finally {
      setIsSearching(false);
    }
  };

  const handleReset = () => {
    setSearchParams({ businessName: '', states: [] });
    setPagination(prev => ({ ...prev, page: 1 }));
    loadOperators(1);
  };

  const handlePageChange = (newPage) => {
    loadOperators(newPage);
  };

  const handleSelectOperator = async (operator) => {
    // If not already assigned, add to quota first
    if (!isOperatorAssigned(operator.operatorId)) {
      try {
        const response = await apiClient.post('/Operators/assign', {
          operatorIds: [operator.operatorId],
        });

        if (response.data && response.data.success) {
          const data = response.data.data;
          
          // Update quota info
          setQuotaInfo({
            totalAssignedThisMonth: data.totalAssignedThisMonth,
            remainingQuota: data.remainingQuota,
          });
          
          // Add to assigned list
          setAssignedOperators([...assignedOperators, operator.operatorId]);
          
          if (data.addedCount > 0) {
            toast.success(`${operator.businessName} added to your quota. Remaining: ${data.remainingQuota}`);
          } else {
            toast.info(response.data.message);
          }
        } else {
          toast.error(response.data?.message || 'Failed to add operator to quota');
          return; // Don't open popup if assignment failed
        }
      } catch (error) {
        console.error('Add to quota error:', error);
        const errorMessage = error.response?.data?.message || 'Failed to add operator to quota';
        toast.error(errorMessage);
        return; // Don't open popup if assignment failed
      }
    }
    
    // Now open the details popup
    handleViewDetails(operator.operatorId);
  };

  const handleViewDetails = async (operatorId) => {
    console.log('Opening details for operator:', operatorId);
    setIsLoadingDetails(true);
    setIsDetailsDialogOpen(true);
    
    try {
      console.log('Fetching operator details from:', `/operators/${operatorId}`);
      const response = await apiClient.get(`/operators/${operatorId}`);
      
      console.log('Operator details response:', response.data);
      
      if (response.data && response.data.success) {
        console.log('Setting operator details:', response.data.data);
        setSelectedOperatorDetails(response.data.data);
      } else {
        console.error('Failed response:', response.data);
        toast.error('Failed to load operator details');
        setIsDetailsDialogOpen(false);
      }
    } catch (error) {
      console.error('Load details error:', error);
      console.error('Error response:', error.response);
      toast.error(`Failed to load operator details: ${error.message}`);
      setIsDetailsDialogOpen(false);
    } finally {
      setIsLoadingDetails(false);
      console.log('Details loading complete, dialog open:', isDetailsDialogOpen);
    }
  };

  const isOperatorAssigned = (operatorId) => {
    return assignedOperators.includes(operatorId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Search & Assign Operators</h1>
          <p className="text-muted-foreground mt-1">
            Browse operators and add them to your subscription quota
          </p>
        </div>

        {/* Quota Info Banner */}
        {quotaInfo.totalAssignedThisMonth > 0 && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Monthly Quota Status</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quotaInfo.totalAssignedThisMonth} assigned this month • {quotaInfo.remainingQuota} remaining
                  </p>
                </div>
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {quotaInfo.remainingQuota} Available
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Search Filters</CardTitle>
            <CardDescription>
              Filter operators by business name or state
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  placeholder="Enter business name..."
                  value={searchParams.businessName}
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, businessName: e.target.value })
                  }
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="state">States (Multi-Select)</Label>
                  <div className="flex items-center gap-2">
                    {searchParams.states.length < allStates.length && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-0 px-2 text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setSearchParams({ 
                          ...searchParams, 
                          states: allStates.map(s => s.code) 
                        })}
                      >
                        Select All
                      </Button>
                    )}
                    {searchParams.states.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-0 px-2 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => setSearchParams({ ...searchParams, states: [] })}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && value !== "placeholder" && !searchParams.states.includes(value)) {
                      setSearchParams({ 
                        ...searchParams, 
                        states: [...searchParams.states, value] 
                      });
                    }
                  }}
                >
                  <SelectTrigger id="state">
                    <SelectValue placeholder={searchParams.states.length > 0 ? `${searchParams.states.length} state(s) selected` : "Select states..."} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>
                      Select a state to add
                    </SelectItem>
                    {allStates.map((state) => (
                      <SelectItem 
                        key={state.code} 
                        value={state.code}
                        disabled={searchParams.states.includes(state.code)}
                      >
                        {state.name} ({state.code})
                        {searchParams.states.includes(state.code) && ' ✓'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {searchParams.states.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {searchParams.states.map((stateCode) => {
                      const state = allStates.find(s => s.code === stateCode);
                      return (
                        <Badge 
                          key={stateCode} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                          onClick={() => {
                            setSearchParams({
                              ...searchParams,
                              states: searchParams.states.filter(s => s !== stateCode)
                            });
                          }}
                        >
                          {state?.name} ({stateCode}) ×
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Show available states from facets */}
              {facets.states.length > 0 && (
                <div>
                  <Label>Available States</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {facets.states.map((facet) => (
                      <Badge key={facet.name} variant="outline">
                        {facet.name} ({facet.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleSearch} disabled={isSearching || isLoading}>
                {isSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleReset} disabled={isLoading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Operators</CardTitle>
                <CardDescription>
                  {isLoading ? 'Loading...' : `Showing ${operators.length} of ${pagination.totalRecords} operators`} • Page {pagination.page} of {pagination.totalPages}
                </CardDescription>
              </div>
              <Badge variant="secondary">
                {assignedOperators.length} Added This Session
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : operators.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No operators found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Try adjusting your search filters
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operator ID</TableHead>
                        <TableHead>License Number</TableHead>
                        <TableHead>Business Name</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {operators.map((operator) => {
                        const isAssigned = isOperatorAssigned(operator.operatorId);
                        return (
                          <TableRow key={operator.operatorId} className={isAssigned ? 'bg-muted/50' : ''}>
                            <TableCell className="font-medium">{operator.operatorId}</TableCell>
                            <TableCell>{operator.licenseNumber || 'N/A'}</TableCell>
                            <TableCell>{operator.businessName}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{operator.state}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant={isAssigned ? "outline" : "default"}
                                  size="sm"
                                  onClick={() => handleSelectOperator(operator)}
                                >
                                  {isAssigned ? (
                                    <>
                                      <Eye className="h-4 w-4 mr-1" />
                                      View Details
                                    </>
                                  ) : (
                                    'Add to Quota'
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page} of {pagination.totalPages} • {pagination.totalRecords} total records
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || isLoading}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages || isLoading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Operator Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={(open) => {
        console.log('Dialog state changing to:', open);
        setIsDetailsDialogOpen(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Operator Details</DialogTitle>
            <DialogDescription>Complete information about the operator</DialogDescription>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : selectedOperatorDetails ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Operator ID</p>
                    <p className="font-medium">{selectedOperatorDetails.operatorId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Number</p>
                    <p className="font-medium">{selectedOperatorDetails.licenseNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Business Name</p>
                    <p className="font-medium">{selectedOperatorDetails.businessName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">DBA Name</p>
                    <p className="font-medium">{selectedOperatorDetails.dbaName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">State</p>
                    <Badge variant="secondary">{selectedOperatorDetails.state}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">County</p>
                    <p className="font-medium">{selectedOperatorDetails.county || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* License Information */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">License Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">License Type</p>
                    <p className="font-medium">{selectedOperatorDetails.licenseType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Designation</p>
                    <p className="font-medium">{selectedOperatorDetails.licenseDesignation || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{selectedOperatorDetails.category || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Status</p>
                    <p className="font-medium">{selectedOperatorDetails.licenseStatus || 'N/A'}</p>
                  </div>
                  {selectedOperatorDetails.issueDate && (
                    <div>
                      <p className="text-sm text-muted-foreground">Issue Date</p>
                      <p className="font-medium">{selectedOperatorDetails.issueDate}</p>
                    </div>
                  )}
                  {selectedOperatorDetails.expirationDate && (
                    <div>
                      <p className="text-sm text-muted-foreground">Expiration Date</p>
                      <p className="font-medium">{selectedOperatorDetails.expirationDate}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedOperatorDetails.phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedOperatorDetails.phone}</p>
                    </div>
                  )}
                  {selectedOperatorDetails.email && (
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedOperatorDetails.email}</p>
                    </div>
                  )}
                  {selectedOperatorDetails.website && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Website</p>
                      <a href={selectedOperatorDetails.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                        {selectedOperatorDetails.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media */}
              {(selectedOperatorDetails.facebook || selectedOperatorDetails.instagram || selectedOperatorDetails.twitter || selectedOperatorDetails.linkedin) && (
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Social Media</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedOperatorDetails.facebook && (
                      <div>
                        <p className="text-sm text-muted-foreground">Facebook</p>
                        <a href={selectedOperatorDetails.facebook} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                    {selectedOperatorDetails.instagram && (
                      <div>
                        <p className="text-sm text-muted-foreground">Instagram</p>
                        <a href={selectedOperatorDetails.instagram} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                    {selectedOperatorDetails.twitter && (
                      <div>
                        <p className="text-sm text-muted-foreground">Twitter</p>
                        <a href={selectedOperatorDetails.twitter} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                    {selectedOperatorDetails.linkedin && (
                      <div>
                        <p className="text-sm text-muted-foreground">LinkedIn</p>
                        <a href={selectedOperatorDetails.linkedin} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                          View Profile
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Contacts */}
              {(selectedOperatorDetails.contactFirstName1 || selectedOperatorDetails.contactFirstName2 || selectedOperatorDetails.contactFirstName3) && (
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Additional Contacts</h3>
                  <div className="space-y-4">
                    {selectedOperatorDetails.contactFirstName1 && (
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium">{selectedOperatorDetails.contactFirstName1} {selectedOperatorDetails.contactLastName1}</p>
                        <p className="text-sm text-muted-foreground">{selectedOperatorDetails.contactType1}</p>
                        {selectedOperatorDetails.contactEmail1 && <p className="text-sm">{selectedOperatorDetails.contactEmail1}</p>}
                        {selectedOperatorDetails.contactPhone1 && <p className="text-sm">{selectedOperatorDetails.contactPhone1}</p>}
                      </div>
                    )}
                    {selectedOperatorDetails.contactFirstName2 && (
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium">{selectedOperatorDetails.contactFirstName2} {selectedOperatorDetails.contactLastName2}</p>
                        <p className="text-sm text-muted-foreground">{selectedOperatorDetails.contactType2}</p>
                        {selectedOperatorDetails.contactEmail2 && <p className="text-sm">{selectedOperatorDetails.contactEmail2}</p>}
                        {selectedOperatorDetails.contactPhone2 && <p className="text-sm">{selectedOperatorDetails.contactPhone2}</p>}
                      </div>
                    )}
                    {selectedOperatorDetails.contactFirstName3 && (
                      <div className="p-3 border rounded-lg">
                        <p className="font-medium">{selectedOperatorDetails.contactFirstName3} {selectedOperatorDetails.contactLastName3}</p>
                        <p className="text-sm text-muted-foreground">{selectedOperatorDetails.contactType3}</p>
                        {selectedOperatorDetails.contactEmail3 && <p className="text-sm">{selectedOperatorDetails.contactEmail3}</p>}
                        {selectedOperatorDetails.contactPhone3 && <p className="text-sm">{selectedOperatorDetails.contactPhone3}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsDetailsDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No details available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
